import { ChakraV3, FloatingPopover } from '@piccolohealth/ui';
import { uuid } from '@piccolohealth/util';
import type { Editor } from '@tiptap/react';
import React from 'react';
import { FaCommentDots } from 'react-icons/fa';
import { useAppContext } from '../../../../hooks/useAppContext';
import { isExtensionEnabled } from '../../utils';

interface CommentFormProps {
  editor: Editor;
  onClose: () => void;
}

export const CommentForm = (props: CommentFormProps) => {
  const { editor, onClose } = props;
  const { user } = useAppContext();
  const [text, setText] = React.useState('');
  // Stable threadID so it doesn't create a new thread if you spam click save
  const threadId = React.useMemo(() => uuid(), []);

  const onSaveComment = React.useCallback(() => {
    const commentThread = {
      id: threadId,
      comments: [
        {
          username: user.name,
          userId: user.id,
          picture: user.picture ?? undefined,
          time: Date.now(),
          content: text,
        },
      ],
    };

    editor.chain().addCommentThread(commentThread).run();
    onClose();
    setText('');
  }, [editor, onClose, text, threadId, user]);

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSaveComment();
      }
    },
    [onSaveComment],
  );

  return (
    <ChakraV3.Stack bg='white' shadow='2xl' px={4} py={3} rounded='lg' w='xl'>
      <ChakraV3.Textarea
        variant='outline'
        border='none'
        placeholder='Add a comment...'
        value={text}
        autoresize
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
      />
      <ChakraV3.HStack>
        <ChakraV3.Spacer />
        <ChakraV3.Button colorPalette='brand' size='sm' onClick={onSaveComment}>
          Save
        </ChakraV3.Button>
      </ChakraV3.HStack>
    </ChakraV3.Stack>
  );
};

interface Props {
  editor: Editor;
}

export const CommentButton = (props: Props) => {
  const { editor } = props;

  if (!isExtensionEnabled(editor, 'comment')) {
    return null;
  }

  return (
    <FloatingPopover
      shouldInitialFocus
      placement='bottom'
      render={({ context }) => (
        <CommentForm editor={editor} onClose={() => context.onOpenChange(false)} />
      )}
    >
      {/* 
      This is a button rendered out as a div to the frontend. Disgusting, but FloatingUI
      has a large bug in Safari. See https://github.com/floating-ui/floating-ui/issues/2533 
      */}
      <ChakraV3.Button
        as='div'
        tabIndex={0}
        variant='ghost'
        size='sm'
        rounded='lg'
        fontWeight='bold'
      >
        <FaCommentDots />
        Comment
      </ChakraV3.Button>
    </FloatingPopover>
  );
};
